import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Slider, FormControl, InputLabel, Select, MenuItem, TextField, InputAdornment, Chip } from '@mui/material';
import { Search, MapPin, ShoppingCart, Star, SlidersHorizontal, X, CheckCircle } from 'lucide-react';
import { AdSpace } from '../../types';
import { spacesAPI } from '../../services/api';
import { useCart } from '../../context/CartContext';
import { PageHeader } from '../../components/common/UIComponents';
import { indianStates, indianCities } from '../../data/dummyData';

const selectSx = {
  fontFamily:'DM Sans,sans-serif', fontSize:13,
  background:'#fff', borderRadius:'12px !important',
  '& .MuiOutlinedInput-notchedOutline':{ borderColor:'var(--border)' },
  '&:hover .MuiOutlinedInput-notchedOutline':{ borderColor:'#d4c4b0' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline':{ borderColor:'var(--brand)' },
};
const labelSx = { fontSize:13, fontFamily:'DM Sans,sans-serif', color:'var(--text-muted)', '&.Mui-focused':{ color:'var(--brand)' } };

const trafficColors: Record<string,{ bg:string; color:string }> = {
  'High':   { bg:'#e8f5ee', color:'#1a7a4a' },
  'Medium': { bg:'#fef6e4', color:'#92610a' },
  'Low':    { bg:'#f3f0ea', color:'#6b5e4e' },
};

const SpaceCard: React.FC<{ space:AdSpace; onView:()=>void; onAddCart:()=>void; inCart:boolean }> = ({ space, onView, onAddCart, inCart }) => {
  const tc = trafficColors[space.trafficPriority];
  return (
    <div className="card-hover" style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:18, overflow:'hidden', cursor:'pointer', boxShadow:'var(--shadow-sm)' }}>
      <div style={{ position:'relative', height:185, overflow:'hidden' }} onClick={onView}>
        <img src={space.images[0]} alt={space.title} style={{ width:'100%', height:'100%', objectFit:'cover', transition:'transform 0.4s ease' }}
          onMouseEnter={e=>(e.currentTarget as HTMLImageElement).style.transform='scale(1.04)'}
          onMouseLeave={e=>(e.currentTarget as HTMLImageElement).style.transform='scale(1)'}/>
        <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 55%)' }}/>
        {space.isFeatured && (
          <div style={{ position:'absolute', top:12, left:12, background:'#e8621a', color:'#fff', padding:'3px 10px', borderRadius:20, fontSize:10, fontFamily:'DM Sans', fontWeight:700, display:'flex', alignItems:'center', gap:4, boxShadow:'0 2px 8px rgba(232,98,26,0.4)' }}>
            <Star size={9} fill="#fff"/> FEATURED
          </div>
        )}
        <div style={{ position:'absolute', top:12, right:12, background:'rgba(255,255,255,0.92)', backdropFilter:'blur(6px)', color:'var(--text-primary)', padding:'3px 10px', borderRadius:20, fontSize:11, fontFamily:'DM Sans', fontWeight:600 }}>
          {space.mediaType}
        </div>
      </div>

      <div style={{ padding:'16px' }}>
        <h3 onClick={onView} style={{ fontWeight:600, fontSize:14, color:'var(--text-primary)', margin:'0 0 5px', lineHeight:1.35, cursor:'pointer' }}>{space.title}</h3>
        <div style={{ display:'flex', alignItems:'center', gap:4, color:'var(--text-muted)', fontSize:12, marginBottom:10 }}>
          <MapPin size={12} color="var(--brand)"/> {space.area}, {space.city}
        </div>
        <div style={{ display:'flex', gap:6, marginBottom:14, flexWrap:'wrap' }}>
          <span style={{ background:'var(--bg-muted)', color:'var(--text-secondary)', padding:'2px 9px', borderRadius:6, fontSize:11, fontWeight:500, fontFamily:'DM Sans' }}>{space.size}</span>
          <span style={{ background:tc.bg, color:tc.color, padding:'2px 9px', borderRadius:6, fontSize:11, fontWeight:600, fontFamily:'DM Sans' }}>{space.trafficPriority} Traffic</span>
          <span style={{ background:'var(--bg-muted)', color:'var(--text-secondary)', padding:'2px 9px', borderRadius:6, fontSize:11, fontFamily:'DM Sans' }}>{space.propertyType === 'Government Contractor' ? 'Govt.' : 'Private'}</span>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ color:'var(--text-muted)', fontSize:11, marginBottom:1 }}>per month</div>
            <div style={{ color:'var(--brand)', fontFamily:'Playfair Display,serif', fontWeight:700, fontSize:20 }}>₹{space.rentAmount.toLocaleString('en-IN')}</div>
          </div>
          <button onClick={inCart ? ()=>{} : onAddCart}
            style={{ background:inCart?'var(--success-bg)':'linear-gradient(135deg,#e8621a,#c2490e)', color:inCart?'var(--success)':'#fff', border: inCart?'1px solid #b8e8ce':'none', borderRadius:10, padding:'8px 14px', fontFamily:'DM Sans', fontWeight:600, fontSize:12, cursor:inCart?'default':'pointer', display:'flex', alignItems:'center', gap:6, boxShadow:inCart?'none':'var(--shadow-brand)', transition:'all 0.2s' }}>
            {inCart?<><CheckCircle size={13}/>Added</>:<><ShoppingCart size={13}/>Add to Cart</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export const SearchSpaces: React.FC = () => {
  const [spaces, setSpaces] = useState<AdSpace[]>([]);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [mediaType, setMediaType] = useState('');
  const [priceRange, setPriceRange] = useState<number[]>([0, 200000]);
  const [showFilters, setShowFilters] = useState(false);
  const { addToCart, items } = useCart();
  const navigate = useNavigate();
  const cartIds = items.map(i => i.space.id);
  const cities = state ? (indianCities[state] || []) : [];

  const load = async () => {
    setLoading(true);
    const data = await spacesAPI.getAll({ state:state||undefined, city:city||undefined, mediaType:mediaType||undefined, minPrice:priceRange[0], maxPrice:priceRange[1] });
    setSpaces(data); setLoading(false);
  };
  useEffect(() => { load(); }, [state, city, mediaType]);

  const activeFilters = [state, city, mediaType].filter(Boolean).length;

  return (
    <div style={{ background:'var(--bg-base)', minHeight:'100vh', padding:'28px 32px' }} className="page-enter">
      <PageHeader title="Search Ad Spaces" subtitle={`${spaces.length} locations available across India`}
        action={<div style={{ background:'var(--brand-light)', border:'1px solid var(--brand-mid)', borderRadius:10, padding:'6px 14px', fontSize:13, color:'var(--brand)', fontWeight:600, fontFamily:'DM Sans' }}>
          {spaces.length} results
        </div>}/>

      {/* Filter Bar */}
      <div style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:16, padding:'16px 20px', marginBottom:24, boxShadow:'var(--shadow-sm)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel sx={labelSx}>State</InputLabel>
              <Select value={state} onChange={e=>{setState(e.target.value);setCity('');}} label="State" sx={selectSx}>
                <MenuItem value="" sx={{ fontSize:13, color:'var(--text-muted)' }}>All States</MenuItem>
                {indianStates.map(s=><MenuItem key={s} value={s} sx={{ fontSize:13 }}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" disabled={!state}>
              <InputLabel sx={labelSx}>City</InputLabel>
              <Select value={city} onChange={e=>setCity(e.target.value)} label="City" sx={selectSx}>
                <MenuItem value="" sx={{ fontSize:13, color:'var(--text-muted)' }}>All Cities</MenuItem>
                {cities.map(c=><MenuItem key={c} value={c} sx={{ fontSize:13 }}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel sx={labelSx}>Media Type</InputLabel>
              <Select value={mediaType} onChange={e=>setMediaType(e.target.value)} label="Media Type" sx={selectSx}>
                <MenuItem value="" sx={{ fontSize:13, color:'var(--text-muted)' }}>All Types</MenuItem>
                {['Billboard','Hoarding','Digital Screen','Flex Banner','Wall Painting'].map(t=><MenuItem key={t} value={t} sx={{ fontSize:13 }}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <button onClick={()=>setShowFilters(!showFilters)} className="btn-ghost" style={{ width:'100%', padding:'9px 14px', fontSize:13, display:'flex', alignItems:'center', justifyContent:'center', gap:7 }}>
              <SlidersHorizontal size={14}/> Price Range {activeFilters>0 && <span style={{ background:'var(--brand)', color:'#fff', borderRadius:10, width:18, height:18, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700 }}>{activeFilters}</span>}
            </button>
          </Grid>

          {showFilters && (
            <Grid item xs={12}>
              <div style={{ paddingTop:8, paddingBottom:4 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                  <span style={{ color:'var(--text-secondary)', fontSize:13, fontWeight:500 }}>Price Range</span>
                  <span style={{ color:'var(--brand)', fontSize:13, fontWeight:600 }}>₹{priceRange[0].toLocaleString('en-IN')} – ₹{priceRange[1].toLocaleString('en-IN')}</span>
                </div>
                <Slider value={priceRange} onChange={(_,v)=>setPriceRange(v as number[])} min={0} max={200000} step={5000}
                  sx={{ color:'var(--brand)', '& .MuiSlider-rail':{ background:'var(--bg-muted)' }, '& .MuiSlider-thumb':{ background:'var(--brand)', boxShadow:'0 2px 8px rgba(232,98,26,0.4)' } }}/>
              </div>
            </Grid>
          )}

          {(state || city || mediaType) && (
            <Grid item xs={12}>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {state && <Chip label={state} onDelete={()=>setState('')} size="small" sx={{ background:'var(--brand-light)', color:'var(--brand)', border:'1px solid var(--brand-mid)', fontFamily:'DM Sans', fontSize:12, fontWeight:600 }}/>}
                {city && <Chip label={city} onDelete={()=>setCity('')} size="small" sx={{ background:'var(--brand-light)', color:'var(--brand)', border:'1px solid var(--brand-mid)', fontFamily:'DM Sans', fontSize:12, fontWeight:600 }}/>}
                {mediaType && <Chip label={mediaType} onDelete={()=>setMediaType('')} size="small" sx={{ background:'var(--brand-light)', color:'var(--brand)', border:'1px solid var(--brand-mid)', fontFamily:'DM Sans', fontSize:12, fontWeight:600 }}/>}
              </div>
            </Grid>
          )}
        </Grid>
      </div>

      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:60 }}>
          <div style={{ textAlign:'center', color:'var(--text-muted)' }}>
            <div style={{ width:40, height:40, border:'3px solid var(--brand-mid)', borderTopColor:'var(--brand)', borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 12px' }}/>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            <p style={{ fontFamily:'DM Sans', fontSize:14 }}>Loading spaces...</p>
          </div>
        </div>
      ) : (
        <Grid container spacing={2.5}>
          {spaces.map(s => (
            <Grid item xs={12} sm={6} lg={4} xl={3} key={s.id}>
              <SpaceCard space={s} onView={()=>navigate(`/brand/spaces/${s.id}`)} onAddCart={()=>addToCart(s)} inCart={cartIds.includes(s.id)}/>
            </Grid>
          ))}
          {spaces.length === 0 && (
            <Grid item xs={12}>
              <div style={{ textAlign:'center', padding:'60px 20px' }}>
                <div style={{ width:64, height:64, borderRadius:'50%', background:'var(--bg-muted)', display:'inline-flex', alignItems:'center', justifyContent:'center', marginBottom:16, color:'var(--text-muted)' }}><Search size={28}/></div>
                <h3 style={{ color:'var(--text-primary)', fontWeight:600, fontSize:17, margin:'0 0 6px' }}>No spaces found</h3>
                <p style={{ color:'var(--text-muted)', fontSize:13 }}>Try adjusting your filters to see more results.</p>
              </div>
            </Grid>
          )}
        </Grid>
      )}
    </div>
  );
};
